<template>
  <page-view :is-loading="loadings.fetching" fill-height>
    <template #header>
      <base-header
        :title="$t('message.pages.trash.title')"
        :menu="pageMenu"
        has-intro
      >
        <template #actions>
          <base-btn
            :data-intro="$t('message.introJS.trash.restore')"
            :disabled="selectedItems.length === 0"
            @click="handleRestoreItems(selectedItems)"
          >
            {{ $t('message.restore') }}
          </base-btn>
        </template>
      </base-header>
    </template>

    <template #content>
      <v-row>
        <v-col cols="auto">
          <!-- text -->
          <p
            :data-intro="
              !selectedItems.length ? $t('message.introJS.trash.message') : null
            "
          >
            {{ $t('message.pages.trash.infoText') }}
          </p>
        </v-col>
        <v-col cols="auto" class="ml-auto">
          <!-- search bar -->
          <v-text-field
            v-model="searchText"
            ref="autoCompleteInput"
            :placeholder="$t('message.pages.trash.searchBarPlaceholder')"
            :data-intro="
              !selectedItems.length ? $t('message.introJS.trash.search') : null
            "
            class="autocomplete"
            hide-details
            dense
            solo
            flat
            @input="doItemsSearch"
          />
        </v-col>
      </v-row>

      <!-- data-table::header -->
      <v-card
        flat
        width="100%"
        height="45px"
        class="data-table__header"
        ref="dataTableHeader"
      >
        <v-row no-gutters>
          <v-card
            class="d-flex align-center justify-center pl-2"
            height="45px"
            flat
            width="60px"
          >
            <v-simple-checkbox
              :color="!checkboxIndeterminateStatus ? 'primary' : ''"
              :indeterminate="checkboxIndeterminateStatus"
              :ripple="false"
              :value="anyItemSelected"
              @input="selectAllItems"
            ></v-simple-checkbox>
          </v-card>
          <v-col class="d-flex align-center justify-start pl-4">
            <v-hover v-slot="{ hover }">
              <v-row no-gutters @click="handleOrderByName">
                <span class="caption mt-1" style="height: 24px">
                  <b>{{ $t('message.pages.videosDataTable.header.name') }}</b>
                </span>
                <v-icon v-if="hover && !nameOrder" color="grey lighten-1"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && nameOrder === 'desc'"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && nameOrder === 'asc'">mdi-menu-up</v-icon>
              </v-row>
            </v-hover>
          </v-col>
          <v-card class="d-flex align-center" height="45px" flat width="135px">
            <v-hover v-slot="{ hover }">
              <v-row no-gutters @click="handleOrderBySize">
                <span class="caption mt-1" style="height: 24px">
                  <b>{{ $t('message.pages.videosDataTable.header.size') }}</b>
                </span>
                <v-icon v-if="hover && !sizeOrder" color="grey lighten-1"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && sizeOrder === 'desc'"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && sizeOrder === 'asc'">mdi-menu-up</v-icon>
              </v-row>
            </v-hover>
          </v-card>
          <v-card class="d-flex align-center" height="45px" flat width="95px">
            <v-hover v-slot="{ hover }">
              <v-row no-gutters @click="handleOrderByTime">
                <span class="caption mt-1" style="height: 24px">
                  <b>{{ $t('message.pages.videosDataTable.header.time') }}</b>
                </span>
                <v-icon v-if="hover && !timeOrder" color="grey lighten-1"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && timeOrder === 'desc'"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && timeOrder === 'asc'">mdi-menu-up</v-icon>
              </v-row>
            </v-hover>
          </v-card>
          <v-card class="d-flex align-center" height="45px" flat width="160px">
            <v-hover v-slot="{ hover }">
              <v-row no-gutters @click="handleOrderByLastModified">
                <span class="caption mt-1" style="height: 24px">
                  <b>{{ $t('message.pages.trash.thirdColumn') }}</b>
                </span>
                <v-icon
                  v-if="hover && !lastModifiedOrder"
                  color="grey lighten-1"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && lastModifiedOrder === 'desc'"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && lastModifiedOrder === 'asc'"
                  >mdi-menu-up</v-icon
                >
              </v-row>
            </v-hover>
          </v-card>
          <v-card
            class="d-flex align-center pl-2"
            height="45px"
            flat
            :width="filePathColumnSize"
          >
            <span class="caption mt-1" style="height: 24px">
              <b>{{ $t('message.pages.trash.fourthColumn') }}</b>
            </span>
          </v-card>
        </v-row>
      </v-card>

      <!-- data-table::content -->
      <vue-virtual-table
        v-if="allItems.length > 0"
        class="pointerTable data-table__content"
        ref="dataTableContent"
        :class="{
          displayNone: loadings.fetching,
          displayBlock: !loadings.fetching,
          virtualTable: $vuetify.theme.dark,
        }"
        :config="tableConfig"
        :data="searchText.length > 0 ? filteredItems : allItems"
        :height="tableHeight"
        :itemHeight="48"
        :minWidth="1000"
        :data-intro="
          !selectedItems.length ? $t('message.introJS.trash.list') : null
        "
        @contextmenu="show"
        @click="handleClickRow"
      >
        <template slot-scope="scope" slot="selectCommon">
          <v-row
            class="pt-1"
            no-gutters
            @click="handleClickRow(scope.row)"
            justify="center"
            align="center"
          >
            <v-simple-checkbox
              color="primary"
              :ripple="false"
              :value="checkIfSelected(scope.row)"
              @input="checkboxSelection(scope.row)"
            ></v-simple-checkbox>
          </v-row>
        </template>
        <template slot-scope="scope" slot="titleCommon">
          <v-row
            no-gutters
            @click="handleClickRow(scope.row)"
            style="max-width: 100%; margin-top: 5px"
            align="center"
          >
            <v-row no-gutters align="center" style="max-width: 100%">
              <v-icon
                style="margin-left: 3px; margin-right: 5px"
                color="turquisePanda"
              >
                {{ scope.row.name ? 'mdi-folder' : 'mdi-play-circle-outline' }}
              </v-icon>
              <span
                class="text-truncate"
                :title="scope.row.name"
                style="max-width: calc(95% - 50px)"
              >
                {{ scope.row.name ? scope.row.name : scope.row.title }}
              </span>
            </v-row>
          </v-row>
        </template>
        <template slot-scope="scope" slot="sizeCommom">
          <v-row no-gutters @click="handleClickRow(scope.row)" justify="start">
            <span class="text-right pr-2" v-if="scope.row.storage_size">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{
                      scope.row.storage_size > 0.01
                        ? calcMegabytes(scope.row.storage_size, 'B')
                        : ''
                    }}
                  </span>
                </template>
                <span
                  >{{ $t('message.storageTooltip') }} <br />
                  {{ $t('message.storageTooltip2') }}<br />
                  {{ $t('message.storageTooltip3') }}</span
                >
              </v-tooltip>
            </span>
          </v-row>
        </template>
        <template slot-scope="scope" slot="timeCommom">
          <v-row no-gutters @click="handleClickRow(scope.row)" justify="start">
            <span class="text-right" v-if="scope.row['length']">
              {{ formatTime(scope.row['length']) }}
            </span>
          </v-row>
        </template>
        <template slot-scope="scope" slot="lastModifiedCommom">
          <v-row no-gutters @click="handleClickRow(scope.row)" justify="start">
            <span class="text-right pr-15">
              {{
                timeLeftToDeletion(new Date(), new Date(scope.row.updated_at))
              }}
            </span>
          </v-row>
        </template>
        <template slot-scope="scope" slot="filePath">
          <v-row no-gutters @click="handleClickRow(scope.row)" justify="start">
            <v-menu
              v-if="checkIfHasParent(scope.row) && getFolderByItem(scope.row)"
              open-on-hover
              left
              nudge-top="10px"
              nudge-right="16px"
            >
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-row
                    no-gutters
                    align="center"
                    justify="start"
                    style="max-width: 160px"
                  >
                    <span class="text-truncate pl-1" style="max-width: 160px">
                      <v-icon small>mdi-folder</v-icon>
                      {{ getFolderByItem(scope.row).name }}
                    </span>
                  </v-row>
                </span>
              </template>
              <v-card
                class="d-flex flex-row align-center justify-center px-2"
                height="42px"
              >
                <v-card
                  v-for="(item, i) in getFolderPath(getFolderByItem(scope.row))"
                  :key="i"
                  flat
                >
                  <v-row no-gutters>
                    <v-hover v-slot="{ hover }">
                      <v-card
                        flat
                        class="pointerTable px-2 py-1"
                        :class="[hover && 'hoveredCard']"
                        :color="hover ? '#eceefe' : ''"
                        @click="handleClickFolderOrigin(item)"
                      >
                        <v-row no-gutters>
                          <v-icon
                            class="mr-1"
                            small
                            :color="hover ? 'primary' : ''"
                            >mdi-folder</v-icon
                          >
                          <span
                            style="font-size: 13px"
                            :class="hover ? 'primary--text' : ''"
                          >
                            {{ item.name }}
                          </span>
                        </v-row>
                      </v-card>
                    </v-hover>
                    <v-icon v-if="!checkIfIsDirectParent(item, scope.row)"
                      >mdi-chevron-right</v-icon
                    >
                  </v-row>
                </v-card>
              </v-card>
            </v-menu>
            <span v-else>
              <v-hover v-slot="{ hover }">
                <v-row no-gutters>
                  <v-card
                    flat
                    class="pointerTable px-1 py-1"
                    :class="[hover && 'hoveredCard']"
                    :color="hover ? '#d8dcfd' : 'transparent'"
                    @click="handleClickFolderOrigin(scope.row)"
                  >
                    <v-icon
                      class="pr-1 pl"
                      small
                      :color="hover ? 'primary' : ''"
                      >mdi-folder</v-icon
                    >
                    <span :class="hover ? 'primary--text' : ''">/</span>
                  </v-card>
                </v-row>
              </v-hover>
            </span>
          </v-row>
        </template>
      </vue-virtual-table>

      <!-- empty -->
      <v-row
        v-else
        class="flex-column"
        no-gutters
        justify="center"
        align="center"
        :data-intro="$t('message.introJS.trash.listEmpty')"
      >
        <img
          v-if="$vuetify.theme.dark"
          class="ml-4"
          src="@/assets/pandaLogo.svg"
          max-width="180"
        />
        <img
          v-else
          class="ml-4"
          src="@/assets/pandaHappy.svg"
          max-width="180"
        />
        <span class="noFilesAddedText">
          {{ $t('message.pages.trash.noItems') }}
        </span>
      </v-row>

      <!-- context menu -->
      <v-menu
        v-model="showMenu"
        min-width="300px"
        :position-x="x"
        :position-y="y"
        absolute
        offset-y
        :transition="null"
      >
        <v-list dense>
          <v-list-item @click="handleLogs(menuItem)">
            <v-list-item-icon>
              <v-icon>mdi-history</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{
              $t('message.pages.historyModal.history')
            }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="handleRestoreItems(menuItem)">
            <v-list-item-icon>
              <v-icon>mdi-file-restore</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{
              $t('message.pages.trash.restoreItem')
            }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- dialog -->
      <restore-modal />
    </template>
  </page-view>
</template>

<script>
// external imports
import VueVirtualTable from 'vue-virtual-table'
import { mapActions, mapMutations, mapState } from 'vuex'
import Fuse from 'fuse.js'

// internal imports
import i18n from '@/translations/lang'
import calcMegabytesMixin from '@/utils/calcMegabytes'
import introMixin from '@/utils/intro.mixin.js'
import formatTimeMixin from '@/utils/formatTime.mixins.js'

// API
import videosApi from '@/services/videosApi'
import foldersApi from '@/services/foldersApi'

// Components
import RestoreModal from './RestoreModal.vue'
import PageView from '@/layout/PageView.vue'

export default {
  name: 'Trash',
  components: {
    VueVirtualTable,
    RestoreModal,
    PageView,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
    BaseBtn: () => import('@/components/BaseBtn.vue'),
  },
  mixins: [calcMegabytesMixin, introMixin, formatTimeMixin],
  data: () => ({
    loadings: {
      fetching: false,
    },
    selectedItems: [],
    nameOrder: 'asc',
    sizeOrder: 'asc',
    timeOrder: 'asc',
    removingOrder: 'asc',
    lastModifiedOrder: 'asc',
    tableHeight: window.innerHeight - 160,
    tableConfig: [
      {
        prop: '_action',
        name: 'action',
        actionName: 'selectCommon',
        width: 60,
        alignItems: 'start',
      },
      {
        prop: '_action',
        name: i18n.t('message.name'),
        actionName: 'titleCommon',
        alignItems: 'start',
      },
      {
        prop: '_action',
        name: i18n.t('message.size'),
        actionName: 'sizeCommom',
        sortable: true,
        width: 130,
        alignItems: 'center',
      },
      {
        prop: '_action',
        actionName: 'timeCommom',
        name: i18n.t('message.time'),
        width: 90,
        alignItems: 'center',
      },
      {
        prop: '_action',
        actionName: 'lastModifiedCommom',
        name: i18n.t('message.lastModification'),
        sortable: true,
        width: 160,
        alignItems: 'center',
      },
      {
        prop: '_action',
        actionName: 'filePath',
        name: i18n.t('message.lastModification'),
        sortable: true,
        width: 180,
        alignItems: 'center',
      },
    ],
    tableArray: [],
    loadedVideos: [],
    loadedFolders: [],
    showMenu: false,
    menuItem: {},
    x: 0,
    y: 0,
    searchText: '',
    filteredItems: [],
    db: null,
    error: {
      getFilesError: false,
      unknownErrorCode: '504',
    },
  }),
  computed: {
    ...mapState('user', {
      allFolders: 'allFolders',
      folders: 'mainFolders',
      deletedFolders: 'deletedFolders',
    }),
    allItems() {
      return this.loadedFolders.concat(this.loadedVideos)
    },
    trashVideosLength() {
      return this.loadedVideos.length
    },
    anyItemSelected() {
      return this.selectedItems.length > 0
    },
    checkboxIndeterminateStatus() {
      return (
        this.selectedItems.length > 0 &&
        this.selectedItems.length < this.allItems.length
      )
    },
    filePathColumnSize() {
      if (this.searchText.length) {
        if (this.filteredItems.length > 10) {
          return '195px'
        } else {
          return '180px'
        }
      } else if (this.allItems.length > 10) {
        return '195px'
      } else {
        return '180px'
      }
    },
    pageMenu() {
      const i18n = this.$i18next
      const path = 'message.pages.video.pageMenu'

      return [
        { title: i18n.t(`${path}.videos`), to: '/videos' },
        { title: i18n.t(`${path}.trash`), to: '/video/trash' },
      ]
    },
  },
  async created() {
    if (!window.db) {
      this.$root.$emit('initDexie')
    }
    await this.getDeletedItems()
    this.checkIfHasQueryString()

    this.setTableHeight()
  },
  mounted() {
    this.$root.$on('trashRemoveVideos', async (videos) => {
      for (let video of videos) {
        this.loadedVideos = this.loadedVideos.filter(
          (item) => item.id != video.id
        )
        await window.db.videos.update(video.id, {
          ...video,
          status: 'CONVERTED',
        })
      }
      this.selectedItems = []
    })
    this.$root.$on('trashRemoveFolder', (folder) => {
      this.loadedFolders = this.loadedFolders.filter(
        (item) => item.id != folder.id
      )
      this.recoverFolder(folder.id)
      this.selectedItems = []
    })
    this.$root.$on('trashMovedRemovedVideos', async () => {
      this.UPDATE_SHOULD_FETCH_ALL_VIDEOS(true)
    })

    this.intro_key = 'trash'
    if (!this.allItems.length) this.setIntroJS({ key: 'trash', delay: 7500 })

    window.addEventListener('resize', this.setTableHeight)

    this.$nextTick(() => {
      this.setTableHeight()
    })
    this.syncListScrollWithHeaderScroll()
  },
  watch: {
    'selectedItems.length': {
      handler(value) {
        if (!value) return

        this.setIntroJS({
          key: 'trash-selected',
          delay: 350,
        })
      },
      deep: true,
      immediate: true,
    },
  },
  beforeDestroy() {
    this.$root.$off('trashRemoveVideos')
    this.$root.$off('trashRemoveFolder')
    window.removeEventListener('resize', this.setTableHeight)
  },
  methods: {
    ...mapMutations('user', [
      'UPDATE_DID_FETCH_VIDEOS',
      'UPDATE_SHOULD_FETCH_ALL_VIDEOS',
      'UPDATE_MAIN_FOLDERS',
      'UPDATE_DELETED_FOLDERS',
      'UPDATE_ALL_FOLDERS',
    ]),
    ...mapActions('user', ['addFolder', 'recoverFolder']),
    syncListScrollWithHeaderScroll() {
      const _this = this
      if (!this.$refs.dataTableContent?.$el) {
        setTimeout(() => {
          _this.syncListScrollWithHeaderScroll()
        }, 100)
        return
      }
      this.$refs.dataTableContent.$el.onscroll = () => {
        _this.$refs.dataTableHeader.$el.scrollLeft =
          this.$refs.dataTableContent.$el.scrollLeft
      }
    },
    setTableHeight() {
      const el = document.querySelector('.page-view__content')
      const height = el.clientHeight

      this.tableHeight = Math.abs(height - 125)
    },
    checkIfIsDirectParent(parentItem, item) {
      if (item.title) {
        return parentItem.id === item.folder_id
      } else {
        return parentItem.id === item.parent_folder_id
      }
    },
    checkIfHasParent(item) {
      if (item.title) {
        return !!item.folder_id
      } else {
        return !!item.parent_folder_id
      }
    },
    getFolderByItem(item) {
      let folderId = ''
      if (item.title) {
        folderId = item.folder_id
      } else {
        folderId = item.parent_folder_id
      }
      let folder = this.findFolderById(folderId)
      return folder
    },
    handleClickFolderOrigin(item) {
      this.$root.$emit('changeNavItem', 0)
      let find = this.getFolderByItem(item)
      if ((item.title && item.folder_id === 'root') || !find) {
        this.$router.push({ name: 'Videos' })
        return
      }
      if (item.status === false) {
        this.handleClickRow(item)
        return
      }
      let folderId = item.folder_id ?? item.id
      this.$router.push({ name: 'Folders', params: { id: folderId } })
    },
    findFolderById(id) {
      return this.allFolders.find((folder) => {
        return folder.id === id
      })
    },
    getFolderPath(folder) {
      let theFolder = this.findFolderById(folder.id)
      let folderArray = [theFolder]
      let searchMoreFolders = true
      do {
        let folder = folderArray[folderArray.length - 1]
        if (folder.parent_folder_id) {
          let parentFolderId = folder.parent_folder_id
          let parentFolder = this.findFolderById(parentFolderId)
          folderArray.push(parentFolder)
        } else {
          searchMoreFolders = false
        }
      } while (searchMoreFolders)
      folderArray = folderArray.reverse()
      return folderArray
    },
    async getDeletedItems() {
      this.loadings.fetching = true
      if (
        !this.$store.state.user.didFetchVideos ||
        this.$store.state.user.shouldFetchAllVideos
      ) {
        await this.readUserFolders()
        await this.getAllAccountVideos()
        this.UPDATE_DID_FETCH_VIDEOS(true)
        this.UPDATE_SHOULD_FETCH_ALL_VIDEOS(false)
      }
      await this.setTrashItems()
      this.loadings.fetching = false
    },
    async getAllAccountVideos() {
      try {
        const ret = await videosApi.getAllVideosAndBin()
        let filteredFoldersIds = []
        let videosResponse = ret.data.videos
        for (let folderId in videosResponse) {
          filteredFoldersIds.push(folderId)
        }
        await window.db.videos.clear()
        for (let folderId of filteredFoldersIds) {
          let arrToBeAdded = ret.data.videos[folderId].map((obj) => ({
            ...obj,
            folder_id: folderId,
          }))
          await window.db.videos.bulkPut(arrToBeAdded)
        }
      } catch (error) {
        this.isFetching = false
        this.error.unknownErrorCode = error.code
        this.error.getFilesError = true
      }
    },
    async readUserFolders() {
      try {
        let ret = await foldersApi.getUserFolders()
        let notDeletedFolders = ret.data.folders.filter(
          (folder) => folder.status != false
        )
        let deletedFolders = ret.data.folders.filter(
          (folder) => folder.status === false
        )
        this.UPDATE_MAIN_FOLDERS(notDeletedFolders)
        this.UPDATE_DELETED_FOLDERS(deletedFolders)
        this.UPDATE_ALL_FOLDERS(ret.data.folders)
      } catch (e) {
        this.error.getFilesError = true
      }
    },
    async setTrashItems() {
      this.loadedFolders = [...this.deletedFolders]
      this.loadedVideos = await window.db.videos
        .where({ status: 'DELETING' })
        .toArray()
    },
    checkIfHasQueryString() {
      let searchQuery = this.$route.query.search
      if (searchQuery) {
        this.searchText = searchQuery
        this.doItemsSearch()
      }
    },
    doItemsSearch() {
      let searchValue = this.searchText
      if (searchValue && searchValue.length > 0) {
        let list = JSON.parse(JSON.stringify(this.allItems))
        let fuseOptions = {
          keys: ['title', 'name', 'id', 'video_external_id', 'description'],
          includeScore: true,
          threshold: 0.6,
        }
        const fuse = new Fuse(list, fuseOptions)
        let searchResult = fuse.search(searchValue)
        const itemsResults = searchResult.map((character) => character.item)
        this.filteredItems = itemsResults
      } else {
        this.filteredItems = []
      }
    },
    handleClickRow(item) {
      this.handleRestoreItems(item, true)
    },
    async handleRestoreItems(content, isCallback) {
      let items = []
      if (typeof content === 'object') items.push(content)
      if (Array.isArray(content)) items = content
      this.$root.$emit('restoreModal', items, isCallback)
    },
    selectAllItems() {
      if (this.selectedItems.length === this.allItems.length) {
        this.selectedItems = []
        return
      }
      this.selectedItems = [...this.allItems]
    },
    handleOrderByName() {
      if (!this.nameOrder || this.nameOrder === 'desc') {
        this.nameOrder = 'asc'
      } else {
        this.nameOrder = 'desc'
      }
      if (this.nameOrder === 'asc') {
        this.loadedVideos.sort((a, b) =>
          a.title.localeCompare(b.title, undefined, { numeric: true })
        )
        this.loadedFolders.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
      if (this.nameOrder === 'desc') {
        this.loadedVideos.sort(
          (a, b) =>
            -a.title.localeCompare(b.title, undefined, { numeric: true })
        )
        this.loadedFolders.sort(
          (a, b) => -a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
    },
    handleOrderByRemoving() {
      if (!this.removingOrder || this.removingOrder === 'desc') {
        this.removingOrder = 'asc'
      } else {
        this.removingOrder = 'desc'
      }
      if (this.removingOrder === 'asc') {
        this.loadedVideos.sort((a, b) => a.removeDate - b.removeDate)
      }
      if (this.removingOrder === 'desc') {
        this.loadedVideos.sort((a, b) => b.removeDate - a.removeDate)
      }
    },
    handleOrderBySize() {
      if (!this.sizeOrder || this.sizeOrder === 'desc') {
        this.sizeOrder = 'asc'
      } else {
        this.sizeOrder = 'desc'
      }
      if (this.sizeOrder === 'asc') {
        this.loadedVideos.sort((a, b) => a.storage_size - b.storage_size)
      }
      if (this.sizeOrder === 'desc') {
        this.loadedVideos.sort((a, b) => b.storage_size - a.storage_size)
      }
    },
    handleOrderByTime() {
      if (!this.timeOrder || this.timeOrder === 'desc') {
        this.timeOrder = 'asc'
      } else {
        this.timeOrder = 'desc'
      }
      if (this.timeOrder === 'asc') {
        this.loadedVideos.sort((a, b) => a.length - b.length)
      }
      if (this.timeOrder === 'desc') {
        this.loadedVideos.sort((a, b) => b.length - a.length)
      }
    },
    handleOrderByLastModified() {
      if (!this.lastModifiedOrder || this.lastModifiedOrder === 'desc') {
        this.lastModifiedOrder = 'asc'
      } else {
        this.lastModifiedOrder = 'desc'
      }
      if (this.lastModifiedOrder === 'asc') {
        this.loadedVideos.sort(
          (a, b) => new Date(a.updated_at) - new Date(b.updated_at)
        )
        this.loadedFolders.sort(
          (a, b) => new Date(a.updated_at) - new Date(b.updated_at)
        )
      }
      if (this.lastModifiedOrder === 'desc') {
        this.loadedVideos.sort(
          (a, b) => new Date(a.updated_at) - new Date(b.updated_at)
        )
        this.loadedFolders.sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        )
      }
    },
    checkIfSelected(item) {
      let foundItem = this.selectedItems.find((searchedItem) => {
        return searchedItem.id === item.id
      })
      if (foundItem) return true
      return false
    },
    checkboxSelection(item) {
      let alreadySelected = this.checkIfSelected(item)
      if (alreadySelected) {
        let newArr = [
          ...this.selectedItems.filter((lookFolder) => {
            return lookFolder.id !== item.id
          }),
        ]
        this.selectedItems = [...newArr]
      } else {
        this.selectedItems.push(item)
      }
    },
    show(item, e) {
      this.menuItem = item
      e.preventDefault()
      this.showMenu = false
      this.x = e.clientX
      this.y = e.clientY
      this.$nextTick(() => {
        this.showMenu = true
      })
    },
    timeLeftToDeletion(start, end) {
      const diffTime = Math.abs(end - start)
      let daysLeft = 30 - Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      if (daysLeft <= 1) return '<1 dia'
      return `${daysLeft} dias`
    },
    handleGoBack() {
      this.$root.$emit('changeNavItem', 0)
      if (window.history.length > 2) {
        this.$router.go(-1)
        return
      }
      this.$router.push({ name: 'Videos' })
    },
    handleLogs(item) {
      this.$root.$emit('openModalItemLogs', item)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/autocomplete';
@import '@/assets/scss/src/data-table';

*::v-deep {
  .header__tabs.v-tabs {
    margin-right: 95px;
  }
}
</style>

<style lang="scss">
.displayBlock {
  display: block;
}

.displayNone {
  display: none;
}

.t-header {
  display: none;
}

.pointerTable {
  cursor: pointer;
}

.truncate {
  max-width: 170px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.removeToolbarLeftPadding {
  .v-toolbar__content,
  .v-toolbar__extension {
    padding-left: 5px;
  }
}
</style>
